import type { Location } from 'react-router-dom';
import { AccountModesEnum, BonusTypeEnum, UserPreferenceEnum } from '@/api';
import { RISK_SCORE_THRESHOLD } from '@/config';
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../store';

const userSelectors = {
  getUserSlice: (state: RootState) => state.user,
  getUserBalance: (state: RootState) => state.user.data?.extBalance || 0,
  getSuspense: (state: RootState) => state.user.suspense,
  getAuth: (state: RootState) => state.user.data !== null,
  checkIfEmailConfirmed: (state: RootState) =>
    state.user.data?.emailStatus === 'confirmed',
  checkIfUserLocked: (state: RootState) => !!state.user.data?.isLocked,
  checkIfEmailCandidate: (state: RootState) =>
    !!state.user.data?.emailChangeCandidate,
  checkIfDevModeUsed: (state: RootState) => !!state.profile.data?.devModeUsed,
  checkIfTermsConfirmRequired: (state: RootState) =>
    !!state.user.data?.termsConfirmRequired,
  complianceActivated: (state: RootState) => !!state.user.data?.useCompliance,
  checkIfCheckOnly: (state: RootState) =>
    state.user.data?.preference === UserPreferenceEnum.CHECK,
  checkIfAiTraceOnly: (state: RootState) =>
    state.user.data?.preference === UserPreferenceEnum.AI_TRACE,
  checkIfAiTraceIncluded: (state: RootState) =>
    state.user.data?.preference === UserPreferenceEnum.AI_TRACE_INCLUDED,
  amlDetailsFor: (state: RootState) => state.user.data?.amlDetailsFor || null,
  traceHashFor: (state: RootState) => state.user.data?.traceHashFor || null,
};

const amlHistorySelectors = {
  getAmlHistorySlice: (state: RootState) => state.amlHistory,
};

const amlCheckSelectors = {
  getAmlCheckSlice: (state: RootState) => state.amlCheck,
};

const twoFASelectors = {
  getTwoFASlice: (state: RootState) => state.twoFA,
};

const locationSelectors = {
  getLocation: (state: RootState) => state.router?.location as Location,
};

const profileSelectors = {
  getProfileSlice: (state: RootState) => state.profile,
  monitoringBonusCount: (state: RootState) => {
    return state.profile?.data?.bonuses.reduce((val: number, bonus) => {
      if (bonus.type === BonusTypeEnum.TYPE_MONITORING) {
        val += Number(bonus.value);
      }
      return val;
    }, 0);
  },
  monitoringRiskThreshold: (state: RootState) => {
    return (
      state.profile?.data?.preferences?.monitoring?.threshold ??
      RISK_SCORE_THRESHOLD
    );
  },
  hasApiAccess: (state: RootState) => {
    return !!state.profile?.data?.accessKey && !!state.profile?.data?.accessId;
  },
  presetedFlowModes: (state: RootState) => {
    return state.profile?.data?.preferences?.flow_modes?.available;
  },
  usageTypeSetting: (state: RootState) => {
    return state.profile?.data?.preferences?.usage;
  },
  webhookUrlSetting: (state: RootState) => {
    return state.profile?.data?.preferences?.webhook?.url;
  },
  hasRiskscoreProfile: (state: RootState) => {
    return state.profile?.data?.preferences?.hasCustomRiskProfile;
  },
  isAccountLiteMode: (state: RootState) => {
    return state.profile?.data?.accountMode === AccountModesEnum.LITE;
  },
  checkIfModeLimited: (state: RootState) =>
    state.profile.data?.accountMode !== AccountModesEnum.PRO_PLUS,
  isPro: (state: RootState) =>
    state.profile.data?.accountMode === AccountModesEnum.PRO,
  isProPlus: (state: RootState) =>
    state.profile.data?.accountMode === AccountModesEnum.PRO_PLUS,
  canDownloadPDFReport: (state: RootState) => {
    return (
      state.profile?.data?.accountMode &&
      state.profile?.data?.accountMode !== AccountModesEnum.LITE
    );
  },
};

const promoHistorySelectors = {
  getPromoHistorySlice: (state: RootState) => state.promoHistory,
};

const investigationHistorySelectors = {
  getInvestigationHistorySlice: (state: RootState) =>
    state.investigationHistory,
};

const serviceOptionsSelectors = {
  getServiceOptionsSelectors: (state: RootState) => state.serviceOptions,
};

const paymentSelectors = {
  getPaymentSlice: (state: RootState) => state.payment,
  getDiscountDueTime: (state: RootState) => state.payment.discountDueTime,
  getPaymentPlans: (state: RootState) => {
    const { paymentPlans } = state.payment;

    if (!paymentPlans) {
      return null;
    }

    return paymentPlans.plans;
  },
};

const blockSelectors = {
  getIsBlocked: (state: RootState) => state.block.isBlocked,
};

const modalSelectors = {
  getPersonalInfoModalSlice: (state: RootState) => state.personalInfoModal,
  getTwoFAActivateModalSlice: (state: RootState) => state.twoFAActivateModal,
  getTwoFADeActivateModalSlice: (state: RootState) =>
    state.twoFADeActivateModal,
  getCriticalBalanceAlertModalSlice: (state: RootState) =>
    state.criticalBalanceAlertModal,
  getShareBalanceModalSlice: (state: RootState) => state.shareBalanceModal,
  getCheckMethodModalSlice: (state: RootState) => state.checkMethodModal,
  getReceiveRemindersModalSlice: (state: RootState) =>
    state.receiveRemindersModal,
};

const checkBatchSelectors = {
  getCheckBatchSlice: (state: RootState) => state.checkBatch,
};

const addressMonitoringSelectors = {
  getAddressMonitoringSlice: (state: RootState) => state.addressMonitoring,
};

const complianceAuthTokenSelectors = {
  getComplianceAuthTokenSelectors: (state: RootState) =>
    state.complianceAuthToken,
};

const complianceStatsSelectors = {
  getComplianceStatsSlice: (state: RootState) => state.complianceStats,
};

const complianceCategoryStatsSelectors = {
  getComplianceCategoryStatsSlice: (state: RootState) =>
    state.complianceCategoryStats,
};

const complianceTransferSelectors = {
  getComplianceTransferSlice: (state: RootState) => state.complianceTransfers,
};

const complianceAlertStatsSelectors = {
  getComplianceAlertStatsSlice: (state: RootState) =>
    state.complianceAlertStats,
};

const complianceAlertsSelectors = {
  getComplianceAlertsSlice: (state: RootState) => state.complianceAlerts,
};

const complianceCustomersSelectors = {
  getComplianceCustomersSlice: (state: RootState) => state.complianceCustomers,
};

const complianceAlertLevelsSelectors = {
  getComplianceAlertLevelsSlice: (state: RootState) =>
    state.complianceAlertLevels,
};

const complianceListsSelectors = {
  getComplianceListsSlice: (state: RootState) => state.complianceLists,
};

const externalServiceSelectors = {
  getExternalServiceSlice: (state: RootState) => state.externalService,
  helpcrunchLoaded: (state: RootState) =>
    state.externalService.helpcrunchLoaded,
};

const riskscoreProfilesSelectors = {
  getRiskscoreProfilesSlice: (state: RootState) => state.riskscoreProfiles,
};

const showDemoDataSelectors = {
  byCompliance: (state: RootState) => {
    return !state.user.data?.useCompliance;
  },
  byMonitoring: (state: RootState) => {
    return !state.profile.data?.usedMonitoring;
  },
};

const getAlertRulesData = createSelector(
  [
    (state: RootState) => state.alertRules.data?.items,
    (state: RootState) => state.alertRules.loading,
  ],
  (items, loading) => ({
    data: items || [],
    loading,
  }),
);

const alertRulesSelectors = {
  getAlertRulesSlice: (state: RootState) => state.alertRules,
  getAlertRules: (state: RootState) => state.alertRules.data?.items || [],
  getAlertRulesTotal: (state: RootState) => state.alertRules.data?.total || 0,
  getAlertRulesError: (state: RootState) => state.alertRules.error,
  getAlertRulesData,
};

const amlTraceSelectors = {
  getTraceSlice: (state: RootState) => state.amlTraces,
  getTraces: (state: RootState) => state.amlTraces.traces,
  getTraceById: (state: RootState, traceId: string) =>
    state.amlTraces.traces[traceId] || [],
  getTracerCurrencies: (state: RootState) => state.amlTraces.currencies,
  getActiveUuid: (state: RootState) => state.amlTraces.activeUuid,
};

const amlTraceHistorySelectors = {
  getTraceHistorySlice: (state: RootState) => state.amlTraceHistory,
};

export {
  userSelectors,
  amlHistorySelectors,
  amlCheckSelectors,
  twoFASelectors,
  locationSelectors,
  profileSelectors,
  promoHistorySelectors,
  investigationHistorySelectors,
  serviceOptionsSelectors,
  paymentSelectors,
  blockSelectors,
  modalSelectors,
  checkBatchSelectors,
  addressMonitoringSelectors,
  complianceAuthTokenSelectors,
  complianceStatsSelectors,
  complianceCategoryStatsSelectors,
  complianceTransferSelectors,
  complianceAlertStatsSelectors,
  complianceAlertsSelectors,
  complianceCustomersSelectors,
  complianceAlertLevelsSelectors,
  complianceListsSelectors,
  externalServiceSelectors,
  riskscoreProfilesSelectors,
  showDemoDataSelectors,
  alertRulesSelectors,
  amlTraceSelectors,
  amlTraceHistorySelectors,
};
